import type { Endpoint, FlowGraph } from '@reelplicate/flows';
import { createContext, use } from 'react';
import { createStore, type StoreApi, useStore } from 'zustand';

import {
  addNode,
  connect,
  type ConnectResult,
  moveNodes,
  removeElements,
  updateNode,
} from './graph-ops';

const HISTORY_LIMIT = 50;
/** Edits to the same thing this close together undo as one (typing, stepping a number). */
const COALESCE_MS = 1000;

export type EditorState = {
  graph: FlowGraph;
  /** Bumped on every graph change; autosave compares it with what it last saved. */
  version: number;
  selectedNodeIds: string[];
  selectedEdgeIds: string[];
  past: FlowGraph[];
  future: FlowGraph[];
  lastEdit: { key: string; at: number } | null;

  addNode: (type: string, position: { x: number; y: number }) => string;
  connect: (source: Endpoint, target: Endpoint) => ConnectResult;
  remove: (nodeIds: readonly string[], edgeIds?: readonly string[]) => void;
  /** Saves the graph as an undo step before a drag moves nodes. */
  beginMove: () => void;
  /** Moves nodes without a history step (see `beginMove`). */
  move: (positions: ReadonlyMap<string, { x: number; y: number }>) => void;
  setConfig: (nodeId: string, config: Record<string, unknown>) => void;
  rename: (nodeId: string, label: string) => void;
  select: (nodeIds: string[], edgeIds?: string[]) => void;
  undo: () => void;
  redo: () => void;
  /** Replaces everything with a graph from the server (after a reload). */
  reset: (graph: FlowGraph) => void;
};

export type EditorStore = StoreApi<EditorState>;

export function createEditorStore(
  initial: FlowGraph,
  now: () => number = Date.now,
): EditorStore {
  return createStore<EditorState>()((set, get) => {
    /** Applies a change as one undo step, or folds it into the last one when `key` repeats. */
    const commit = (graph: FlowGraph, key?: string) => {
      const { graph: before, past, lastEdit, version } = get();
      const at = now();
      const fold =
        key !== undefined && lastEdit?.key === key && at - lastEdit.at < COALESCE_MS;
      set({
        graph,
        version: version + 1,
        past: fold ? past : [...past, before].slice(-HISTORY_LIMIT),
        future: [],
        lastEdit: key ? { key, at } : null,
      });
    };
    const dropMissingSelection = (graph: FlowGraph) => {
      const nodes = new Set(graph.nodes.map((node) => node.id));
      const edges = new Set(graph.edges.map((edge) => edge.id));
      const { selectedNodeIds, selectedEdgeIds } = get();
      set({
        selectedNodeIds: selectedNodeIds.filter((id) => nodes.has(id)),
        selectedEdgeIds: selectedEdgeIds.filter((id) => edges.has(id)),
      });
    };

    return {
      graph: initial,
      version: 0,
      selectedNodeIds: [],
      selectedEdgeIds: [],
      past: [],
      future: [],
      lastEdit: null,

      addNode: (type, position) => {
        const result = addNode(get().graph, type, position);
        commit(result.graph);
        set({ selectedNodeIds: [result.id], selectedEdgeIds: [] });
        return result.id;
      },
      connect: (source, target) => {
        const result = connect(get().graph, source, target);
        if (result.ok) commit(result.graph);
        return result;
      },
      remove: (nodeIds, edgeIds = []) => {
        if (nodeIds.length === 0 && edgeIds.length === 0) return;
        const graph = removeElements(get().graph, nodeIds, edgeIds);
        commit(graph);
        dropMissingSelection(graph);
      },
      beginMove: () => {
        const { graph, past } = get();
        set({ past: [...past, graph].slice(-HISTORY_LIMIT), future: [], lastEdit: null });
      },
      move: (positions) => {
        set({ graph: moveNodes(get().graph, positions), version: get().version + 1 });
      },
      setConfig: (nodeId, config) =>
        commit(updateNode(get().graph, nodeId, { config }), `config:${nodeId}`),
      rename: (nodeId, label) =>
        commit(updateNode(get().graph, nodeId, { label }), `label:${nodeId}`),
      select: (selectedNodeIds, selectedEdgeIds = []) =>
        set({ selectedNodeIds, selectedEdgeIds }),
      undo: () => {
        const { past, graph, future, version } = get();
        const previous = past.at(-1);
        if (!previous) return;
        set({
          graph: previous,
          past: past.slice(0, -1),
          future: [graph, ...future],
          version: version + 1,
          lastEdit: null,
        });
        dropMissingSelection(previous);
      },
      redo: () => {
        const { past, graph, future, version } = get();
        const next = future[0];
        if (!next) return;
        set({
          graph: next,
          past: [...past, graph],
          future: future.slice(1),
          version: version + 1,
          lastEdit: null,
        });
        dropMissingSelection(next);
      },
      reset: (graph) =>
        set({
          graph,
          version: 0,
          past: [],
          future: [],
          lastEdit: null,
          selectedNodeIds: [],
          selectedEdgeIds: [],
        }),
    };
  });
}

export const EditorStoreContext = createContext<EditorStore | null>(null);

export function useEditorStore(): EditorStore {
  const store = use(EditorStoreContext);
  if (!store) throw new Error('useEditor must be used inside the flow editor');
  return store;
}

export function useEditor<T>(selector: (state: EditorState) => T): T {
  return useStore(useEditorStore(), selector);
}
